import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
	AdressverwaltungFacade,
	AdressverwaltungService,
	ASK_SWITCH_UNSAVED_KONTAKT_IN_EDIT,
	generateSearchString,
	KontaktInVerfahren,
	KontaktKategorie,
	KontaktKategorieLabel,
	KontaktLinkRel,
	KontaktResource,
	KontaktSearchMode,
	VerfahrenKontaktLinkRel,
	VerfahrenKontaktResource,
} from '@schir-int-client/adressverwaltung-shared';
import { Adressat, AdressatDragData, AdressatFacade } from '@schir-int-client/aufgabe-shared';
import { hasLink } from '@ngxp/rest';
import { VerfahrenLinkRel, VerfahrenResource } from '@schir-int-client/verfahren-shared';
import {
	ClipboardService,
	DialogService,
	HandlesBackdropClickAndEscapeKey,
	KontaktAktenzeichenDialogComponent,
	NotizDialogComponent,
} from '@schir-int-client/tech';
import { Subject, Subscription } from 'rxjs';
import { AdressverwaltungDialogComponent } from '../../adressverwaltung-dialog/adressverwaltung-dialog.component';
import { first, take } from 'rxjs/operators';
import { MailAddressService } from '../../../../../../tech/src/lib/mail-address/mail-address.service';
import { DialogDataYesNo } from '@schir-int-client/dialog-shared';
import { isNil } from 'lodash-es';
import { getIdNumber } from '../../../../../../test-helpers/src/lib/resource-helper';

@Component({
	selector: 'schir-int-client-kontakt-mit-kategorien',
	templateUrl: './kontakt-mit-kategorien.component.html',
	styleUrls: ['./kontakt-mit-kategorien.component.scss'],
	standalone: false,
})
export class KontaktMitKategorienComponent
	extends HandlesBackdropClickAndEscapeKey<
		AdressverwaltungDialogComponent | NotizDialogComponent | KontaktAktenzeichenDialogComponent
	>
	implements OnInit, OnDestroy
{
	@Input() kontaktInVerfahren: KontaktInVerfahren;

	@Input() verfahren: VerfahrenResource;

	@Output() unassignKontakt: EventEmitter<VerfahrenKontaktResource> = new EventEmitter();

	expand: boolean;
	draggable: boolean;
	kategorieLabel = KontaktKategorieLabel;
	kontakt: KontaktResource;
	inVerfahren: VerfahrenKontaktResource;

	private subscriptions: Subscription[] = [];
	audioAlert: Subject<string> = new Subject<string>();

	constructor(
		protected dialogService: DialogService,
		private adressatFacade: AdressatFacade,
		private adressverwaltungService: AdressverwaltungService,
		private adressverwaltungFacade: AdressverwaltungFacade,
		private clipboardService: ClipboardService,
		private mailAddressService: MailAddressService
	) {
		super(dialogService);
	}

	ngOnInit() {
		this.kontakt = this.kontaktInVerfahren.kontakt;
		this.inVerfahren = this.kontaktInVerfahren.inVerfahren;
		const updateDraggable = (targetDisplayed: boolean) =>
			(this.draggable = targetDisplayed && this.kontakt.active && hasLink(this.kontakt, KontaktLinkRel.EDIT));
		this.subscriptions.push(this.adressverwaltungService.adressatenColDisplayed.subscribe(updateDraggable));

		updateDraggable(!!document.querySelector('schir-int-client-adressaten-in-aufgabe'));
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	drag(event: DragEvent) {
		const dragData: AdressatDragData = {
			kontaktId: getIdNumber(this.kontakt),
			name: this.kontakt.name,
			vorname: this.kontakt.vorname,
			firmenName: this.kontakt.firmenName,
		};
		const data = JSON.stringify(dragData);
		event.dataTransfer.setData('text/plain', data);
	}

	get allowsUnassign(): boolean {
		return hasLink(this.verfahren, VerfahrenLinkRel.SET_KONTAKT_ASSIGNED_IN_KATEGORIEN);
	}

	unassign(kategorie: KontaktKategorie): void {
		const newAssignment = {
			...this.kontaktInVerfahren.inVerfahren,
			kategorien: this.kontaktInVerfahren.inVerfahren.kategorien.filter(k => k !== kategorie),
		};
		this.unassignKontakt.emit(newAssignment);
		this.audioAlert.next('Der Kontakt wurde aus dem Verfahren entfernt.');
	}

	openKontaktInAdressverwaltung(kontakt: KontaktResource) {
		this.matDialogRef = this.dialogService.openEditorDialog(this, AdressverwaltungDialogComponent, {
			panelClass: 'adressverwaltung',
		});

		const editor: AdressverwaltungDialogComponent = <AdressverwaltungDialogComponent>(
			this.matDialogRef.componentInstance
		);

		const searchString = generateSearchString(kontakt);

		if (this.kontakt.active) {
			editor.searchParameters = {
				searchString: searchString,
				searchModes: [
					KontaktSearchMode.NATUERLICH,
					KontaktSearchMode.JURISTISCH,
					KontaktSearchMode.BEHOERDEN,
					KontaktSearchMode.AKTIV,
				],
			};
		} else {
			editor.searchParameters = {
				searchString: searchString,
				searchModes: [
					KontaktSearchMode.NATUERLICH,
					KontaktSearchMode.JURISTISCH,
					KontaktSearchMode.BEHOERDEN,
					KontaktSearchMode.INAKTIV,
				],
			};
		}

		this.adressverwaltungFacade.setSelectedKontakt(kontakt);
		editor.setSelectedKontakt({ kontakt: kontakt, editMode: false });
		this.adressverwaltungFacade.setEditMode(this.kontakt.active);
	}

	protected closeAndSetActiveEditorNull() {
		this.subscriptions.push(
			this.adressverwaltungFacade.selectedKontakt.pipe(first()).subscribe(() => {
				this.adressverwaltungFacade.setEditMode(false);
				this.matDialogRef.close();
			})
		);
	}

	assignKontakt() {
		const adressat: Adressat = {
			channelBrief: true,
			channelFax: false,
			channelEmail: false,
			channelAbholung: false,
			channelEgvp: false,
			notiz: '',
		};
		this.adressatFacade.createAdressat(adressat, getIdNumber(this.kontakt));
		this.audioAlert.next('Der Kontakt wurde als Adressat hinzugefügt.');
	}

	openVerfahrenKontaktNotizDialog() {
		const name = this.kontakt.juristischePerson ? this.kontakt.firmenName : this.kontakt.name;

		const title = 'Notiz zum Kontakt "' + name + '"';

		this.matDialogRef = this.dialogService.openEditorDialog(this, NotizDialogComponent, {
			data: {
				title: title,
				notiz: this.inVerfahren.notiz,
			},
		});

		const editor: NotizDialogComponent = <NotizDialogComponent>this.matDialogRef.componentInstance;
		this.subscriptions.push(
			editor.notizEvent.pipe(take(1)).subscribe((data: string) => {
				this.updateVerfahrenKontaktNotiz(data);
			})
		);
	}

	openKontaktAktenzeichenDialog() {
		const name = this.kontakt.juristischePerson ? this.kontakt.firmenName : this.kontakt.name;

		const title = 'Aktenzeichen für Kontakt "' + name + '"';

		this.matDialogRef = this.dialogService.openEditorDialog(this, KontaktAktenzeichenDialogComponent, {
			data: {
				title: title,
				kontaktAktenzeichen: this.inVerfahren.aktenzeichen,
			},
		});

		const editor: KontaktAktenzeichenDialogComponent = <KontaktAktenzeichenDialogComponent>(
			this.matDialogRef.componentInstance
		);
		this.subscriptions.push(
			editor.kontaktAktenzeichenEvent.pipe(take(1)).subscribe((data: string) => {
				this.updateVerfahrenKontaktAktenzeichen(data);
			})
		);
	}

	updateVerfahrenKontaktNotiz(notiz: string) {
		const updated: VerfahrenKontaktResource = { ...this.kontaktInVerfahren.inVerfahren, notiz };
		this.adressverwaltungFacade.updateVerfahrenKontaktFreiText(updated);
	}

	updateVerfahrenKontaktAktenzeichen(aktenzeichen: string) {
		const updated: VerfahrenKontaktResource = { ...this.kontaktInVerfahren.inVerfahren, aktenzeichen };
		this.adressverwaltungFacade.updateVerfahrenKontaktFreiText(updated);
	}

	openFinanzSanktionsListe() {
		const parameters = this.kontakt.juristischePerson
			? [this.kontakt.firmenName]
			: [this.kontakt.name, this.kontakt.vorname];
		const searchQuery = parameters.filter(x => !isNil(x)).join(' ');
		const url =
			'https://www.finanz-sanktionsliste.de/fisalis/?txtSearch=' + searchQuery + '&cmdSearch=%F0%9F%94%8D';
		this.dialogService
			.openDialogYesNo(
				'Sie sind dabei, die Anwendung zu verlassen und folgende externe Seite aufzurufen. Wollen Sie fortfahren?',
				['Ja', 'Nein'],
				[],
				[url]
			)
			.pipe(first())
			.subscribe((data: DialogDataYesNo) => {
				if (data && data.agree) {
					window.open(url, '_blank');
				}
			});
	}

	getConfirmMessage(): string {
		return ASK_SWITCH_UNSAVED_KONTAKT_IN_EDIT;
	}

	copyMailAddressToClipboard(kontakt: KontaktResource) {
		const mailAddress = this.mailAddressService.createMailAddressForKontakt(kontakt);
		this.clipboardService.copyAndShowSnackBar(mailAddress, 'Adressdaten wurden in die Zwischenablage kopiert.');
	}

	protected readonly KontaktLinkRel = KontaktLinkRel;
	protected readonly VerfahrenKontaktLinkRel = VerfahrenKontaktLinkRel;
}
